import { useEffect, useState } from "react";
import type { KnowledgeItem } from "@note-app/contracts";
import { Button, Dialog, Select, Switch } from "@note-app/ui";
import type {
  CatalogSnapshot,
  ItemMetadataInput,
} from "../view-model.ts";

export interface ItemMetadataDialogProps {
  readonly item: KnowledgeItem | undefined;
  readonly catalog?: CatalogSnapshot;
  readonly saving: boolean;
  readonly error?: string;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSave: (input: ItemMetadataInput) => void | Promise<void>;
}

const FAVORITE_PROPERTY = "fengyue.favorite";
const STATUS_PROPERTY = "fengyue.status";
const COVER_PROPERTY = "fengyue.cover";
const DOCUMENT_DATE_PROPERTY = "fengyue.documentDate";
const CUSTOM_PREFIX = "fengyue.custom.";

interface CustomFieldDraft {
  readonly id: string;
  readonly key: string;
  readonly value: string;
}

export function ItemMetadataDialog({
  item,
  catalog,
  saving,
  error,
  onOpenChange,
  onSave,
}: ItemMetadataDialogProps) {
  const [folderId, setFolderId] = useState("");
  const [tagIds, setTagIds] = useState<readonly string[]>([]);
  const [favorite, setFavorite] = useState(false);
  const [icon, setIcon] = useState("");
  const [status, setStatus] = useState("draft");
  const [cover, setCover] = useState("");
  const [documentDate, setDocumentDate] = useState("");
  const [customFields, setCustomFields] = useState<readonly CustomFieldDraft[]>([]);

  useEffect(() => {
    setFolderId(item?.folderId ?? "");
    setTagIds(item?.tags ?? []);
    setFavorite(item?.properties[FAVORITE_PROPERTY] === true);
    setIcon(item?.icon ?? "");
    setStatus(stringProperty(item, STATUS_PROPERTY) || "draft");
    setCover(stringProperty(item, COVER_PROPERTY));
    setDocumentDate(stringProperty(item, DOCUMENT_DATE_PROPERTY));
    setCustomFields(
      Object.entries(item?.properties ?? {})
        .filter(([key]) => key.startsWith(CUSTOM_PREFIX))
        .map(([key, value]) => ({ id: key, key: key.slice(CUSTOM_PREFIX.length), value: displayProperty(value) })),
    );
  }, [item]);

  return (
    <Dialog
      open={item !== undefined}
      onOpenChange={onOpenChange}
      title={item ? `整理“${item.title}”` : "整理笔记"}
      description="直接编辑图标、状态、封面、文件夹、标签和自定义字段。系统创建时间保持不可变，便于可靠排序与审计。"
      closeLabel="关闭整理窗口"
      footer={
        <div className="knowledge-dialog-actions">
          <Button disabled={saving} onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button
            variant="primary"
            disabled={saving || !item || !catalog}
            onClick={() => {
              const properties = { ...(item?.properties ?? {}) };
              properties[STATUS_PROPERTY] = status;
              if (cover.trim()) properties[COVER_PROPERTY] = cover.trim();
              else delete properties[COVER_PROPERTY];
              if (documentDate) properties[DOCUMENT_DATE_PROPERTY] = documentDate;
              else delete properties[DOCUMENT_DATE_PROPERTY];
              for (const key of Object.keys(properties)) {
                if (key.startsWith(CUSTOM_PREFIX)) delete properties[key];
              }
              for (const field of customFields) {
                const key = field.key.trim();
                if (key) properties[`${CUSTOM_PREFIX}${key}`] = parseProperty(field.value);
              }
              void onSave({
                folderId: folderId || null,
                tagIds,
                favorite,
                icon: icon.trim() || null,
                properties,
              });
            }}
          >
            {saving ? "保存中…" : "保存整理"}
          </Button>
        </div>
      }
    >
      <div className="knowledge-metadata-form">
        <div className="knowledge-metadata-form__identity">
          <label><span>图标</span><input value={icon} maxLength={12} placeholder="例如：🌙" disabled={saving} onChange={(event) => setIcon(event.currentTarget.value)} /></label>
          <Select label="状态" value={status} disabled={saving} onChange={(event) => setStatus(event.currentTarget.value)}>
            <option value="draft">草稿</option><option value="active">进行中</option><option value="review">待复核</option><option value="done">已完成</option><option value="archived">已归档</option>
          </Select>
        </div>
        <label className="knowledge-metadata-form__field"><span>封面</span><input value={cover} placeholder="图片地址或 CSS 颜色，例如 #8b5cf6" disabled={saving} onChange={(event) => setCover(event.currentTarget.value)} /></label>
        {cover ? <div className="knowledge-metadata-cover" aria-label="封面预览" style={coverStyle(cover)} /> : null}
        <label className="knowledge-metadata-form__field"><span>文档日期</span><input type="date" value={documentDate} disabled={saving} onChange={(event) => setDocumentDate(event.currentTarget.value)} /><small>这是可编辑的业务日期；系统创建时间不会被改写。</small></label>
        <Select
          label="文件夹"
          value={folderId}
          disabled={saving || !catalog}
          onChange={(event) => setFolderId(event.currentTarget.value)}
        >
          <option value="">无文件夹</option>
          {catalog?.folders.map((folder) => (
            <option key={folder.id} value={folder.id}>
              {folder.name}
            </option>
          ))}
        </Select>
        <fieldset disabled={saving || !catalog}>
          <legend>标签</legend>
          {catalog?.tags.length ? (
            <div className="knowledge-tag-options">
              {catalog.tags.map((tag) => (
                <label key={tag.id}>
                  <input
                    type="checkbox"
                    checked={tagIds.includes(tag.id)}
                    onChange={(event) =>
                      setTagIds((current) =>
                        event.currentTarget.checked
                          ? [...new Set([...current, tag.id])]
                          : current.filter((id) => id !== tag.id),
                      )
                    }
                  />
                  <span
                    className="knowledge-tag-dot"
                    style={{ background: tag.color ?? "var(--knowledge-accent)" }}
                    aria-hidden="true"
                  />
                  {tag.name}
                </label>
              ))}
            </div>
          ) : (
            <p>还没有标签，可在“整理”页面创建。</p>
          )}
        </fieldset>
        <Switch
          label="加入收藏"
          description="收藏内容会出现在所有设备的收藏视图中。"
          checked={favorite}
          disabled={saving}
          onCheckedChange={setFavorite}
        />
        <fieldset disabled={saving}>
          <legend>自定义字段</legend>
          <div className="knowledge-custom-field-list">
            {customFields.map((field) => (
              <div key={field.id}>
                <input aria-label="字段名称" value={field.key} placeholder="字段名" onChange={(event) => setCustomFields((current) => current.map((entry) => entry.id === field.id ? { ...entry, key: event.currentTarget.value } : entry))} />
                <input aria-label={`${field.key || "自定义"}字段值`} value={field.value} placeholder="文本、数字、true 或 JSON" onChange={(event) => setCustomFields((current) => current.map((entry) => entry.id === field.id ? { ...entry, value: event.currentTarget.value } : entry))} />
                <button type="button" aria-label={`删除${field.key || "自定义"}字段`} onClick={() => setCustomFields((current) => current.filter((entry) => entry.id !== field.id))}>×</button>
              </div>
            ))}
          </div>
          <Button size="small" variant="ghost" onClick={() => setCustomFields((current) => [...current, { id: crypto.randomUUID(), key: "", value: "" }])}>添加字段</Button>
        </fieldset>
        {error ? <p className="knowledge-form-error" role="alert">{error}</p> : null}
      </div>
    </Dialog>
  );
}

function stringProperty(item: KnowledgeItem | undefined, key: string): string {
  const value = item?.properties[key];
  return typeof value === "string" ? value : "";
}

function displayProperty(value: unknown): string {
  return typeof value === "string" ? value : JSON.stringify(value);
}

function parseProperty(value: string): import("@note-app/contracts").JsonValue {
  const trimmed = value.trim();
  if (!trimmed) return "";
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (parsed === null || ["string", "number", "boolean"].includes(typeof parsed) || Array.isArray(parsed) || typeof parsed === "object") {
      return parsed as import("@note-app/contracts").JsonValue;
    }
  } catch { /* Plain text is a valid field value. */ }
  return value;
}

function coverStyle(value: string): React.CSSProperties {
  return /^(#|rgb|hsl|oklch|linear-gradient)/iu.test(value.trim())
    ? { background: value }
    : { backgroundImage: `linear-gradient(90deg, rgba(15,23,42,.15), transparent), url(${JSON.stringify(value)})` };
}
